import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ArrowLeft, AlertTriangle, Eye, Lock, Share2, 
  UserX, Twitter, Instagram, Facebook, Linkedin, 
  CheckCircle2, LogOut, History, Plus, Shield
} from "lucide-react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { ForcefieldLogo } from "@/components/ForcefieldLogo";

// Constants
const HIGH_RISK_THRESHOLD = 70;
const MEDIUM_RISK_THRESHOLD = 40;

// Icon mappings
const CATEGORY_ICONS = {
  "Privacy Settings": Eye,
  "Information Exposure": Share2,
  "Account Vulnerability": Lock,
  "Cross-Platform Risk": UserX,
  "Social Engineering": AlertTriangle
};

const PLATFORM_ICONS = {
  "Twitter": Twitter,
  "Instagram": Instagram,
  "Facebook": Facebook,
  "LinkedIn": Linkedin
};

export default function DashboardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedItems, setCheckedItems] = useState({});

  const fetchAnalysis = useCallback(async () => {
    try {
      const response = await fetch(`${API}/analysis/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error("Analysis not found");
      }
      
      const data = await response.json();
      setAnalysis(data);
      
      const initial = {};
      data.vectors.forEach((_, i) => {
        initial[i] = false;
      });
      setCheckedItems(initial);
    } catch (error) {
      toast.error(error.message);
      navigate("/analyze");
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  const getCategoryIcon = useCallback((category) => {
    return CATEGORY_ICONS[category] || Shield;
  }, []);

  const getPlatformIcon = useCallback((platform) => {
    return PLATFORM_ICONS[platform] || Shield;
  }, []);

  const getRiskColor = useCallback((level) => {
    const levelLower = level.toLowerCase();
    const colors = {
      high: "bg-red-500/10 text-red-400 border-red-500/20",
      medium: "bg-amber-500/10 text-amber-400 border-amber-500/20",
      low: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
    };
    return colors[levelLower] || "bg-gray-500/10 text-gray-400 border-gray-500/20";
  }, []);

  const getRiskScoreColor = useCallback((score) => {
    if (score >= HIGH_RISK_THRESHOLD) return "text-red-400";
    if (score >= MEDIUM_RISK_THRESHOLD) return "text-amber-400";
    return "text-emerald-400";
  }, []);

  const getRiskProgressColor = useCallback((level) => {
    const levelLower = level.toLowerCase();
    const colors = {
      high: "bg-red-500",
      medium: "bg-amber-500",
      low: "bg-emerald-500"
    };
    return colors[levelLower] || "bg-gray-500";
  }, []);

  const handleCheckChange = useCallback((index, checked) => {
    setCheckedItems(prev => ({ ...prev, [index]: checked }));
  }, []);

  const { completedCount, totalItems, progressPercent } = useMemo(() => {
    if (!analysis) return { completedCount: 0, totalItems: 0, progressPercent: 0 };
    const total = analysis.vectors.length;
    const completed = Object.values(checkedItems).filter(Boolean).length;
    return {
      completedCount: completed,
      totalItems: total,
      progressPercent: total > 0 ? (completed / total) * 100 : 0
    };
  }, [analysis, checkedItems]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500/20 border-t-violet-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!analysis) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="dashboard-logo-link">
            <ForcefieldLogo className="w-10 h-10" />
            <span className="font-outfit font-bold text-xl text-white tracking-tight">Social <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Forcefield</span></span>
          </Link>
          <nav className="flex items-center gap-4">
            <Link to="/analyze">
              <Button variant="ghost" className="text-gray-400 hover:text-white gap-2" data-testid="new-analysis-btn">
                <Plus className="w-4 h-4" />
                New Scan
              </Button>
            </Link>
            <Link to="/history">
              <Button variant="ghost" className="text-gray-400 hover:text-white gap-2" data-testid="dashboard-history-btn">
                <History className="w-4 h-4" />
                History
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              onClick={logout}
              className="text-gray-400 hover:text-white gap-2"
              data-testid="dashboard-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </nav>
        </div>
      </header>

      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-[400px] h-[400px] bg-violet-600/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-1/4 w-[300px] h-[300px] bg-fuchsia-600/5 rounded-full blur-[100px]" />
      </div>

      {/* Main content */}
      <main className="pt-28 pb-16 px-6 relative">
        <div className="max-w-6xl mx-auto">
          {/* Back button */}
          <Link to="/analyze" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors" data-testid="back-to-analyze-link">
            <ArrowLeft className="w-4 h-4" />
            New Scan
          </Link>

          {/* Summary Section */}
          <div className="grid lg:grid-cols-12 gap-6 mb-8">
            {/* Risk Score Card */}
            <div className="lg:col-span-4 p-8 rounded-2xl bg-white/[0.02] border border-white/5">
              <p className="text-sm text-gray-500 uppercase tracking-widest mb-4">Threat Level</p>
              <div className="flex items-end gap-4 mb-6">
                <span className={`font-outfit text-6xl font-bold ${getRiskScoreColor(analysis.overall_risk_score)}`}>
                  {analysis.overall_risk_score}
                </span>
                <span className="text-gray-500 text-2xl mb-2">/100</span>
              </div>
              <div className="relative h-2 bg-white/5 rounded-full overflow-hidden mb-4">
                <div 
                  className={`h-full ${getRiskProgressColor(analysis.overall_risk_level)} transition-all duration-500`}
                  style={{ width: `${analysis.overall_risk_score}%` }}
                />
              </div>
              <Badge className={`${getRiskColor(analysis.overall_risk_level)} border uppercase text-xs`}>
                {analysis.overall_risk_level} Risk
              </Badge>
            </div>

            {/* Summary Card */}
            <div className="lg:col-span-8 p-8 rounded-2xl bg-white/[0.02] border border-white/5">
              <p className="text-sm text-gray-500 uppercase tracking-widest mb-4">Analysis Summary</p>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">{analysis.summary}</p>
              
              {/* Handles analyzed */}
              <div className="flex flex-wrap gap-3">
                {analysis.handles.twitter && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10">
                    <Twitter className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-300 font-mono text-sm">@{analysis.handles.twitter}</span>
                  </div>
                )}
                {analysis.handles.instagram && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10">
                    <Instagram className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-300 font-mono text-sm">@{analysis.handles.instagram}</span>
                  </div>
                )}
                {analysis.handles.facebook && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10">
                    <Facebook className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-300 font-mono text-sm">{analysis.handles.facebook}</span>
                  </div>
                )}
                {analysis.handles.linkedin && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10">
                    <Linkedin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-300 font-mono text-sm">{analysis.handles.linkedin}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 mb-8">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-300 font-medium">Remediation Progress</p>
              <span className="text-gray-500 font-mono text-sm">{completedCount}/{totalItems} completed</span>
            </div>
            <Progress value={progressPercent} className="h-2 bg-white/5" />
          </div>

          {/* Security Vectors */}
          <div className="mb-8">
            <p className="text-sm text-gray-500 uppercase tracking-widest mb-6">Security Vectors</p>
            <div className="grid md:grid-cols-2 gap-4 stagger-children">
              {analysis.vectors.map((vector, index) => {
                const CategoryIcon = getCategoryIcon(vector.category);
                const PlatformIcon = getPlatformIcon(vector.platform);
                const vectorKey = `${vector.category}-${vector.platform}-${index}`;
                
                return (
                  <div 
                    key={vectorKey}
                    className={`p-6 rounded-2xl bg-white/[0.02] border border-white/5 opacity-0 animate-fade-up ${checkedItems[index] ? 'opacity-50' : ''}`}
                    data-testid={`vector-card-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        id={`vector-${index}`}
                        checked={checkedItems[index]}
                        onCheckedChange={(checked) => handleCheckChange(index, checked)}
                        className="mt-1 border-white/20 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                        data-testid={`vector-checkbox-${index}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge className={`${getRiskColor(vector.risk_level)} border uppercase text-xs`}>
                            {vector.risk_level}
                          </Badge>
                          <div className="flex items-center gap-1 text-gray-500">
                            <CategoryIcon className="w-3 h-3" />
                            <span className="text-xs">{vector.category}</span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-500">
                            <PlatformIcon className="w-3 h-3" />
                            <span className="text-xs">{vector.platform}</span>
                          </div>
                        </div>
                        <h3 className={`font-outfit font-semibold text-white mb-2 ${checkedItems[index] ? 'line-through text-gray-500' : ''}`}>
                          {vector.title}
                        </h3>
                        <p className="text-gray-400 text-sm mb-4">{vector.description}</p>
                        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-300 text-sm">{vector.recommendation}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4">
            <Link to="/analyze">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white px-8 rounded-full" data-testid="run-new-analysis-btn">
                Run New Scan
              </Button>
            </Link>
            <Link to="/history">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 rounded-full" data-testid="view-history-btn">
                View History
              </Button>
            </Link>
          </div>

          {/* Powered by */}
          <p className="mt-12 text-center text-gray-600 text-sm">
            powered by <a href="https://agentseven.ai" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">AgentSeven.ai</a>
          </p>
        </div>
      </main>
    </div>
  );
}
